import { createContext, useContext, type ReactNode } from 'react'

import type { PageMeta } from './seo'

/**
 * Collects the meta of the page being rendered, for the prerender step.
 *
 * It is a setter rather than a mutable field so that nothing writes through a
 * context value directly — that is the kind of hidden mutation that makes a
 * render pass impossible to reason about.
 */
export interface MetaSink {
  set: (meta: PageMeta) => void
  get: () => PageMeta | null
}

/** Creates a sink that simply remembers the last page rendered. */
export function createMetaSink(): MetaSink {
  let current: PageMeta | null = null
  return {
    set: (meta) => {
      current = meta
    },
    get: () => current,
  }
}

const MetaSinkContext = createContext<MetaSink | null>(null)

export function MetaSinkProvider({ sink, children }: { sink: MetaSink; children: ReactNode }) {
  return <MetaSinkContext.Provider value={sink}>{children}</MetaSinkContext.Provider>
}

export function useMetaSink(): MetaSink | null {
  return useContext(MetaSinkContext)
}
